import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Card, Modal, Button } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getTemplateOptionsForExperienceLevel,
  normalizeExperienceLevel,
} from "../NewResume/templateCatalog";
import TemplateLivePreview from "../NewResume/TemplateLivePreview";
import { SAMPLE_RESUME_DATA } from "../NewResume/sampleResumeData";
import { useResumeStore } from "../../store/resumeStore";
import { isResumeEmpty } from "./mapParsedResumeToState";
import useViewportWidth from "../../hooks/useViewportWidth";
import { withTemplateIdInSearch } from "../NewResume/templateSelection";

export default function TemplateGallery() {
  const navigate = useNavigate();
  const viewportWidth = useViewportWidth();
  const isCompact = viewportWidth <= 768;
  const isSmallScreen = viewportWidth <= 1024;
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [open, setOpen] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const injectedSampleRef = useRef(false);
  const previousResumeRef = useRef(null);
  const setResume = useResumeStore((state) => state.setResume);
  const setSelectedTemplateId = useResumeStore(
    (state) => state.setSelectedTemplateId,
  );
  const experienceLevel = normalizeExperienceLevel(
    sessionStorage.getItem("experienceLevel"),
  );
  const filteredTemplates = getTemplateOptionsForExperienceLevel(experienceLevel, {
    smallScreen: isSmallScreen,
  });
  const experienceLabel =
    experienceLevel === "fresher"
      ? "Fresher"
      : experienceLevel === "experienced"
        ? "Experienced"
        : null;

  useEffect(() => {
    const currentResume = useResumeStore.getState().resume;

    if (!isResumeEmpty(currentResume)) {
      setPreviewReady(true);
      return undefined;
    }

    previousResumeRef.current = currentResume;
    injectedSampleRef.current = true;
    setResume(SAMPLE_RESUME_DATA);
    setPreviewReady(true);

    return () => {
      if (injectedSampleRef.current && previousResumeRef.current) {
        useResumeStore.getState().setResume(previousResumeRef.current);
      }
    };
  }, [setResume]);

  const handleSelect = (template) => {
    setSelectedTemplate(template);
    setOpen(true);
  };

  const handleUseTemplate = () => {
    setOpen(false);
    setSelectedTemplateId(selectedTemplate.id, "/templates");
    navigate(
      {
        pathname: "/export",
        search: withTemplateIdInSearch("", selectedTemplate.id),
      },
      {
        state: {
          templateId: selectedTemplate.id,
        },
      },
    );
  };

  return (
    <div
      style={{
        padding: isCompact ? 16 : 40,
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <h1 style={{ fontSize: isCompact ? 28 : 32, fontWeight: 700, marginBottom: 8 }}>
        Choose a Resume Template
      </h1>
      <p style={{ color: "#64748b", marginBottom: isCompact ? 20 : 32 }}>
        {experienceLabel
          ? `Showing ${experienceLabel.toLowerCase()} templates based on your selected experience level`
          : "Pick a template to start building your resume"}
      </p>

      <Row gutter={[isCompact ? 16 : 24, isCompact ? 16 : 24]}>
        {filteredTemplates.map((template) => (
          <Col key={template.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              cover={
                previewReady ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: 14,
                      background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
                    }}
                  >
                    <TemplateLivePreview templateId={template.id} variant="card" />
                  </div>
                ) : null
              }
              onClick={() => handleSelect(template)}
            >
              <Card.Meta
                title={template.name}
                description={template.description}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* 🔹 Preview Modal */}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={isCompact ? "calc(100vw - 24px)" : 900}
      >
        {selectedTemplate && (
          <Row gutter={24}>
            <Col xs={24} md={14}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: isCompact ? 12 : 16,
                  borderRadius: 12,
                  background: "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
                }}
              >
                <TemplateLivePreview
                  templateId={selectedTemplate.id}
                  variant="modal"
                  borderRadius={12}
                />
              </div>
            </Col>

            <Col xs={24} md={10}>
              <h2 style={{ marginTop: isCompact ? 16 : 0 }}>{selectedTemplate.name}</h2>
              <p style={{ color: "#64748b" }}>
                {selectedTemplate.description}
              </p>

              <Button
                type="primary"
                size="large"
                block
                style={{ marginTop: 24 }}
                onClick={handleUseTemplate}
              >
                Use this template
              </Button>

              <Button
                block
                style={{ marginTop: 12 }}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
}
